import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, ArrowUp, ArrowDown, Trash2, Plus, Star, Sparkles, Layers, Search, ChevronDown, Eye, EyeOff } from 'lucide-react';
import { getCatalogue, adminGetShowcase, adminPutShowcase, adminGetHomeCollections, adminPutHomeCollections } from '../../services/cms';
import { useToast } from '../../hooks/use-toast';

const PALETTE = ['#ed5c1f', '#12b3a6', '#f5a623', '#6d4aff', '#e0457b', '#2b6cff', '#0aa06e'];

const AddPicker = ({ options, disabledSlugs, onAdd, testid }) => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return options.filter((o) => !disabledSlugs.has(o.slug) && (!s || o.titre.toLowerCase().includes(s))).slice(0, 8);
  }, [q, options, disabledSlugs]);
  return (
    <div className="relative">
      <button onClick={() => setOpen((v) => !v)} data-testid={`${testid}-toggle`} className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary font-800 text-sm hover:bg-primary/20"><Plus className="w-4 h-4" /> Ajouter un webtoon</button>
      {open && (
        <div className="absolute z-20 mt-2 w-80 max-w-[90vw] bg-card border border-border rounded-2xl shadow-xl p-3">
          <div className="relative mb-2"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" /><input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un titre..." data-testid={`${testid}-search`} className="w-full pl-9 pr-3 py-2 rounded-xl bg-muted font-600 outline-none text-sm" /></div>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {filtered.length === 0 ? <p className="text-sm text-muted-foreground font-600 px-2 py-3">Aucun webtoon disponible.</p> : filtered.map((o) => (
              <button key={o.slug} onClick={() => { onAdd(o.slug); setOpen(false); setQ(''); }} data-testid={`${testid}-opt-${o.slug}`} className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-muted text-left">
                <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted shrink-0">{o.featured_image?.url && <img src={o.featured_image.url} alt="" className="w-full h-full object-cover" />}</div>
                <span className="font-700 text-sm line-clamp-2">{o.titre}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SlotItem = ({ item, index, total, onMove, onRemove, onChange, testid }) => {
  const [openOv, setOpenOv] = useState(false);
  const img = item.image_override || item.default_image;
  const titre = item.titre_override || item.default_titre || item.slug;
  const sub = item.sous_titre_override || item.default_sous_titre || '';
  return (
    <div className="bg-card border border-border rounded-2xl p-3" data-testid={`${testid}-${item.slug}`}>
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-xl overflow-hidden bg-muted shrink-0">{img && <img src={img} alt="" className="w-full h-full object-cover" />}</div>
        <div className="flex-1 min-w-0">
          <p className="font-700 text-sm line-clamp-1">{titre}</p>
          {sub && <p className="text-xs text-muted-foreground font-600 line-clamp-1">{sub}</p>}
          {!item.exists && <p className="text-xs text-destructive font-800">⚠ Webtoon introuvable / non public</p>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={() => onMove(index, -1)} disabled={index === 0} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center disabled:opacity-30" data-testid={`${testid}-up-${item.slug}`}><ArrowUp className="w-4 h-4" /></button>
          <button onClick={() => onMove(index, 1)} disabled={index === total - 1} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center disabled:opacity-30" data-testid={`${testid}-down-${item.slug}`}><ArrowDown className="w-4 h-4" /></button>
          <button onClick={() => onRemove(index)} className="w-8 h-8 rounded-full bg-muted hover:bg-destructive hover:text-white flex items-center justify-center" data-testid={`${testid}-remove-${item.slug}`}><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <button onClick={() => setOpenOv((v) => !v)} className="mt-2 text-xs font-800 text-primary flex items-center gap-1"><ChevronDown className={`w-3 h-3 transition-transform ${openOv ? 'rotate-180' : ''}`} /> Surcharges éditoriales (optionnel)</button>
      {openOv && (
        <div className="mt-2 grid sm:grid-cols-3 gap-2">
          <input value={item.titre_override || ''} onChange={(e) => onChange(index, 'titre_override', e.target.value)} placeholder="Titre (défaut : réel)" className="px-3 py-2 rounded-lg bg-muted text-sm font-600 outline-none" />
          <input value={item.sous_titre_override || ''} onChange={(e) => onChange(index, 'sous_titre_override', e.target.value)} placeholder="Sous-titre" className="px-3 py-2 rounded-lg bg-muted text-sm font-600 outline-none" />
          <input value={item.image_override || ''} onChange={(e) => onChange(index, 'image_override', e.target.value)} placeholder="Image (URL)" className="px-3 py-2 rounded-lg bg-muted text-sm font-600 outline-none" />
        </div>
      )}
    </div>
  );
};

const SlotList = ({ title, icon: Icon, items, setItems, max, options, extraDisabled, testid }) => {
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; setItems(c); };
  const remove = (i) => setItems(items.filter((_, k) => k !== i));
  const change = (i, key, val) => { const c = [...items]; c[i] = { ...c[i], [key]: val }; setItems(c); };
  const add = (slug) => {
    if (items.length >= max) return;
    const o = options.find((x) => x.slug === slug);
    setItems([...items, { slug, titre_override: '', sous_titre_override: '', image_override: '', exists: true, default_titre: o?.titre, default_image: o?.featured_image?.url, default_sous_titre: (o?.auteurs || []).map((a) => a.name).join(', ') }]);
  };
  const disabled = new Set([...items.map((i) => i.slug), ...(extraDisabled || [])]);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="font-display font-700 flex items-center gap-2"><Icon className="w-5 h-5 text-primary" /> {title} <span className="text-sm text-muted-foreground font-700">({items.length}/{max})</span></h4>
        {items.length < max && <AddPicker options={options} disabledSlugs={disabled} onAdd={add} testid={`${testid}-add`} />}
      </div>
      {items.length === 0 ? <p className="text-sm text-muted-foreground font-600">Aucun emplacement. Ajoutez un webtoon.</p>
        : items.map((it, i) => <SlotItem key={it.slug} item={it} index={i} total={items.length} onMove={move} onRemove={remove} onChange={change} testid={`${testid}-item`} />)}
    </div>
  );
};

const CollectionsButtons = () => {
  const [buttons, setButtons] = useState(null);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  useEffect(() => { adminGetHomeCollections().then((d) => setButtons(d.buttons || [])).catch(() => setButtons([])); }, []);
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= buttons.length) return; const c = [...buttons]; [c[i], c[j]] = [c[j], c[i]]; setButtons(c); };
  const upd = (i, key, val) => { const c = [...buttons]; c[i] = { ...c[i], [key]: val }; setButtons(c); };
  const save = async () => { setSaving(true); try { await adminPutHomeCollections({ buttons: buttons.map((b) => ({ slug: b.slug, color: b.color, actif: b.actif })) }); toast({ title: 'Boutons Collections enregistrés' }); } finally { setSaving(false); } };
  if (!buttons) return <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>;
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground font-600">Ordre, couleur et visibilité des boutons Collections affichés sur l’accueil.</p>
      {buttons.map((b, i) => (
        <div key={b.slug} className="bg-card border border-border rounded-2xl p-3 flex items-center gap-3" data-testid={`coll-item-${b.slug}`}>
          <span className="px-4 py-2 rounded-full text-white font-800 text-sm shrink-0" style={{ background: b.color }}>{b.name}</span>
          <span className="text-xs text-muted-foreground font-700">{b.count} webtoons</span>
          <div className="ml-auto flex items-center gap-1">
            <div className="flex gap-1 mr-2">
              {PALETTE.map((p) => <button key={p} onClick={() => upd(i, 'color', p)} className={`w-6 h-6 rounded-full border-2 ${b.color === p ? 'border-foreground' : 'border-transparent'}`} style={{ background: p }} data-testid={`coll-color-${b.slug}-${p}`} />)}
            </div>
            <button onClick={() => upd(i, 'actif', !b.actif)} className={`w-8 h-8 rounded-full flex items-center justify-center ${b.actif ? 'bg-secondary/20 text-secondary' : 'bg-muted text-muted-foreground'}`} data-testid={`coll-toggle-${b.slug}`}>{b.actif ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
            <button onClick={() => move(i, -1)} disabled={i === 0} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
            <button onClick={() => move(i, 1)} disabled={i === buttons.length - 1} className="w-8 h-8 rounded-full bg-muted flex items-center justify-center disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
      <button onClick={save} disabled={saving} data-testid="coll-save-btn" className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-800 text-sm disabled:opacity-60">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer les boutons</button>
    </div>
  );
};

const HomeShowcasePanel = () => {
  const [tab, setTab] = useState('vitrine');
  const [options, setOptions] = useState([]);
  const [favoris, setFavoris] = useState(null);
  const [decouverte, setDecouverte] = useState([]);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    getCatalogue().then((l) => setOptions((l || []).filter((e) => e.slug && e.featured_image?.url))).catch(() => setOptions([]));
    adminGetShowcase().then((d) => { setFavoris(d.favoris || []); setDecouverte(d.decouverte || []); }).catch(() => { setFavoris([]); setDecouverte([]); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const strip = (l) => l.map((i) => ({ slug: i.slug, titre_override: i.titre_override || '', sous_titre_override: i.sous_titre_override || '', image_override: i.image_override || '' }));
      await adminPutShowcase({ favoris: strip(favoris), decouverte: strip(decouverte) });
      toast({ title: 'Vitrine enregistrée', description: 'Les blocs Favoris et À découvrir aussi sont à jour.' });
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (d?.code === 'duplicate_with_favoris') toast({ title: 'Doublon interdit', description: `Déjà dans les favoris : ${(d.slugs || []).join(', ')}`, variant: 'destructive' });
      else if (d?.code === 'invalid_slugs') toast({ title: 'Webtoon invalide', description: `${(d.slugs || []).join(', ')}`, variant: 'destructive' });
      else toast({ title: 'Erreur', description: 'Enregistrement impossible.', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (favoris === null) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-5" data-testid="home-showcase-panel">
      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-800 font-600">
        Choisissez à la main les webtoons de l’accueil. « Nos webtoons favoris » = 4 emplacements ; « À découvrir aussi » = 8 emplacements (jamais un favori). Par défaut on affiche les vraies données Citytoon ; les surcharges (titre/sous-titre/image) sont optionnelles.
      </div>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setTab('vitrine')} data-testid="showcase-tab-vitrine" className={`px-4 py-2 rounded-full text-sm font-800 ${tab === 'vitrine' ? 'bg-primary text-white' : 'bg-muted'}`}>Favoris & À découvrir</button>
        <button onClick={() => setTab('collections')} data-testid="showcase-tab-collections" className={`px-4 py-2 rounded-full text-sm font-800 ${tab === 'collections' ? 'bg-primary text-white' : 'bg-muted'}`}>Boutons Collections</button>
      </div>

      {tab === 'vitrine' ? (
        <div className="space-y-8">
          <SlotList title="Nos webtoons favoris" icon={Star} items={favoris} setItems={setFavoris} max={4} options={options} testid="fav" />
          <SlotList title="À découvrir aussi" icon={Sparkles} items={decouverte} setItems={setDecouverte} max={8} options={options} extraDisabled={favoris.map((f) => f.slug)} testid="disc" />
          <button onClick={save} disabled={saving} data-testid="showcase-save-btn" className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-800 disabled:opacity-60">{saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />} Enregistrer la vitrine</button>
        </div>
      ) : (
        <CollectionsButtons />
      )}
    </div>
  );
};

export default HomeShowcasePanel;
